// frontend/src/pages/admin/TodoList.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useCouple } from '../../contexts/CoupleContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import Button from '../../components/common/Button';
//import Card from '../../components/common/Card';
import Alert from '../../components/common/Alert';
import Loader from '../../components/common/Loader';
import EmptyState from '../../components/common/EmptyState';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import TodoService from '../../services/todo.service';
import { Todo, TodoPriority } from '../../types';
import { CheckSquare, Plus, Edit2, Trash2 } from 'react-feather';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const priorityColors = {
  [TodoPriority.HIGH]: 'text-red-700 bg-red-50 border-red-200',
  [TodoPriority.MEDIUM]: 'text-yellow-700 bg-yellow-50 border-yellow-200',
  [TodoPriority.LOW]: 'text-blue-700 bg-blue-50 border-blue-200',
};

const TodoList: React.FC = () => {
  const { currentCouple, loading: coupleLoading } = useCouple();
  
  const [todos, setTodos] = useState<Todo[]>([]);
  const [filteredTodos, setFilteredTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending'>('all');
  const [priorityFilter, setPriorityFilter] = useState<TodoPriority | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentTodo, setCurrentTodo] = useState<Todo | null>(null);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [priority, setPriority] = useState<TodoPriority>(TodoPriority.MEDIUM);
  const [assignedTo, setAssignedTo] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  useEffect(() => {
    if (currentCouple) {
      fetchTodos();
    }
  }, [currentCouple]);
  
  useEffect(() => {
    applyFilters();
  }, [todos, statusFilter, priorityFilter, searchQuery]);
  
  const fetchTodos = async () => {
    if (!currentCouple) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const fetchedTodos = await TodoService.getCoupleTodos(currentCouple._id);
      setTodos(fetchedTodos);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch todos';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const applyFilters = () => {
    let result = [...todos];
    
    // Apply status filter
    if (statusFilter === 'completed') {
      result = result.filter(todo => todo.completed);
    } else if (statusFilter === 'pending') {
      result = result.filter(todo => !todo.completed);
    }
    
    // Apply priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(todo => todo.priority === priorityFilter);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        todo => 
          todo.title.toLowerCase().includes(query) || 
          (todo.description && todo.description.toLowerCase().includes(query))
      );
    }
    
    // Sort by due date and priority
    result.sort((a, b) => {
      // First sort by completion status
      if (a.completed && !b.completed) return 1;
      if (!a.completed && b.completed) return -1;
      
      // Then sort by due date
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      
      if (dateA !== dateB) return dateA - dateB;
      
      // Then sort by priority (high to low)
      const priorityValues = { [TodoPriority.HIGH]: 0, [TodoPriority.MEDIUM]: 1, [TodoPriority.LOW]: 2 };
      return priorityValues[a.priority] - priorityValues[b.priority];
    });
    
    setFilteredTodos(result);
  };
  
  const handleAddTodo = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!currentCouple) return;
    
    setFormLoading(true);
    setFormError(null);
    
    try {
      const newTodo = await TodoService.createTodo({
        title,
        description: description || undefined,
        dueDate: dueDate ? dueDate.toISOString() : undefined,
        priority,
        couple: currentCouple._id,
        assignedTo: assignedTo || undefined,
      });
      
      setTodos(prev => [...prev, newTodo]);
      resetForm();
      setIsAddModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add todo';
      setFormError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleEditTodo = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!currentCouple || !currentTodo) return;
    
    setFormLoading(true);
    setFormError(null);
    
    try {
      const updatedTodo = await TodoService.updateTodo(currentTodo._id, {
        title,
        description: description || undefined,
        dueDate: dueDate ? dueDate.toISOString() : undefined,
        priority,
        assignedTo: assignedTo || undefined,
      });
      
      setTodos(prev => prev.map(todo => todo._id === updatedTodo._id ? updatedTodo : todo));
      resetForm();
      setIsEditModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update todo';
      setFormError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleDeleteTodo = async () => {
    if (!currentTodo) return;
    
    setFormLoading(true);
    setFormError(null);
    
    try {
      await TodoService.deleteTodo(currentTodo._id);
      setTodos(prev => prev.filter(todo => todo._id !== currentTodo._id));
      setIsDeleteModalOpen(false);
      setCurrentTodo(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete todo';
      setFormError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleToggleTodoStatus = async (todo: Todo) => {
    try {
      let updatedTodo: Todo; // Explicitly type the variable
      
      if (todo.completed) {
        updatedTodo = await TodoService.uncompleteTodo(todo._id);
      } else {
        updatedTodo = await TodoService.completeTodo(todo._id);
      }
      
      setTodos(prev => prev.map(t => t._id === updatedTodo._id ? updatedTodo : t));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update todo status';
      setError(errorMessage);
    }
  };
  
  const openEditModal = (todo: Todo) => {
    setCurrentTodo(todo);
    setTitle(todo.title);
    setDescription(todo.description || '');
    setDueDate(todo.dueDate ? new Date(todo.dueDate) : null);
    setPriority(todo.priority as unknown as TodoPriority);
    setAssignedTo(todo.assignedTo || '');
    setIsEditModalOpen(true);
  };
  
  const openDeleteModal = (todo: Todo) => {
    setCurrentTodo(todo);
    setIsDeleteModalOpen(true);
  };
  
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDueDate(null);
    setPriority(TodoPriority.MEDIUM);
    setAssignedTo('');
    setCurrentTodo(null);
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  if (coupleLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full py-20">
          <Loader size="lg" label="Loading..." />
        </div>
      </AdminLayout>
    );
  }
  
  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full py-20">
          <p className="text-gray-500">Please select a couple from the sidebar</p>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Todo List
              </h2>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <Button
                onClick={() => {
                  resetForm();
                  setIsAddModalOpen(true);
                }}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Add Task
              </Button>
            </div>
          </div>
          
          {error && (
            <Alert
              type="error"
              message={error}
              dismissible
              onDismiss={() => setError(null)}
              className="mt-4"
            />
          )}
          
          <div className="mt-4 bg-white shadow rounded-lg overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b border-gray-200 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div className="sm:col-span-2">
                  <label htmlFor="search" className="sr-only">
                    Search
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      id="search"
                      className="focus:ring-pink-500 focus:border-pink-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Search tasks"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    id="status-filter"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                  >
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="priority-filter" className="block text-sm font-medium text-gray-700">
                    Priority
                  </label>
                  <select
                    id="priority-filter"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value === 'all' ? 'all' : (e.target.value as unknown as TodoPriority))}
                  >
                    <option value="all">All</option>
                    <option value={TodoPriority.HIGH}>High</option>
                    <option value={TodoPriority.MEDIUM}>Medium</option>
                    <option value={TodoPriority.LOW}>Low</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Task List */}
            <div className="bg-white overflow-hidden">
              {loading ? (
                <div className="p-6 flex justify-center">
                  <Loader size="lg" label="Loading tasks..." />
                </div>
              ) : filteredTodos.length === 0 ? (
                <EmptyState
                  icon={<CheckSquare className="h-6 w-6" />}
                  title="No tasks found"
                  description={
                    todos.length === 0
                      ? "You haven't added any tasks yet."
                      : "No tasks match your current filters."
                  }
                  buttonText={todos.length === 0 ? "Add your first task" : undefined}
                  onButtonClick={todos.length === 0 ? () => setIsAddModalOpen(true) : undefined}
                />
              ) : (
                <ul className="divide-y divide-gray-200">
                  {filteredTodos.map((todo) => (
                    <li key={todo._id} className={`p-4 hover:bg-gray-50 ${todo.completed ? 'bg-gray-50' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 pt-0.5">
                            <input
                              type="checkbox"
                              className="h-5 w-5 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                              checked={todo.completed}
                              onChange={() => handleToggleTodoStatus(todo)}
                            />
                          </div>
                          <div className="ml-3">
                            <p className={`text-sm font-medium ${todo.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                              {todo.title}
                            </p>
                            {todo.description && (
                              <p className={`mt-1 text-sm ${todo.completed ? 'text-gray-400' : 'text-gray-500'}`}>
                                {todo.description}
                              </p>
                            )}
                            <div className="mt-2 flex items-center space-x-4">
                              {todo.dueDate && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <svg className="mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                  </svg>
                                  Due: {formatDate(todo.dueDate)}
                                </div>
                              )}
                              
                              {todo.assignedTo && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <svg className="mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                  </svg>
                                  {todo.assignedTo}
                                </div>
                              )}
                              
                              <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[todo.priority]}`}>
                                {todo.priority.charAt(0).toUpperCase() + todo.priority.slice(1)} Priority
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex space-x-2">
                          <button
                            type="button"
                            className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                            onClick={() => openEditModal(todo)}
                          >
                            <Edit2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            onClick={() => openDeleteModal(todo)}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add Task Modal */}
      <Modal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Task"
      >
        {formError && (
          <Alert
            type="error"
            message={formError}
            dismissible
            onDismiss={() => setFormError(null)}
            className="mb-4"
          />
        )}
        
        <form onSubmit={handleAddTodo} className="space-y-4">
          <Input
            id="title"
            label="Task Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          
          <Textarea
            id="description"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
          
          <div>
            <label htmlFor="due-date" className="block text-sm font-medium text-gray-700">
              Due Date
            </label>
            <div className="mt-1">
              <DatePicker
                id="due-date"
                selected={dueDate}
                onChange={(date: Date) => setDueDate(date)}
                dateFormat="MMMM d, yyyy"
                className="shadow-sm focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholderText="Select a date"
                isClearable
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
              Priority
            </label>
            <select
              id="priority"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
              value={priority}
              onChange={(e) => setPriority(e.target.value as unknown as TodoPriority)}
            >
              <option value={TodoPriority.HIGH}>High</option>
              <option value={TodoPriority.MEDIUM}>Medium</option>
              <option value={TodoPriority.LOW}>Low</option>
            </select>
          </div>
          
          <Input
            id="assigned-to"
            label="Assigned To"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            placeholder="e.g. Sarah or Michael"
          />
          
          <div className="flex justify-end space-x-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={formLoading}
              disabled={formLoading || !title}
            >
              Add Task
            </Button>
          </div>
        </form>
      </Modal>
      
      {/* Edit Task Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Task"
      >
        {formError && (
          <Alert
            type="error"
            message={formError}
            dismissible
            onDismiss={() => setFormError(null)}
            className="mb-4"
          />
        )}
        
        <form onSubmit={handleEditTodo} className="space-y-4">
          <Input
            id="edit-title"
            label="Task Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          
          <Textarea
            id="edit-description"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
          
          <div>
            <label htmlFor="edit-due-date" className="block text-sm font-medium text-gray-700">
              Due Date
            </label>
            <div className="mt-1">
              <DatePicker
                id="edit-due-date"
                selected={dueDate}
                onChange={(date: Date) => setDueDate(date)}
                dateFormat="MMMM d, yyyy"
                className="shadow-sm focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholderText="Select a date"
                isClearable
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="edit-priority" className="block text-sm font-medium text-gray-700">
              Priority
            </label>
            <select
             // frontend/src/pages/admin/TodoList.tsx (continued)
             id="edit-priority"
             className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
             value={priority}
             onChange={(e) => setPriority(e.target.value as unknown as TodoPriority)}
           >
             <option value={TodoPriority.HIGH}>High</option>
             <option value={TodoPriority.MEDIUM}>Medium</option>
             <option value={TodoPriority.LOW}>Low</option>
           </select>
         </div>
         
         <Input
           id="edit-assigned-to"
           label="Assigned To"
           value={assignedTo}
           onChange={(e) => setAssignedTo(e.target.value)}
           placeholder="e.g. Sarah or Michael"
         />
         
         <div className="flex justify-end space-x-3 pt-2">
           <Button
             variant="outline"
             onClick={() => setIsEditModalOpen(false)}
           >
             Cancel
           </Button>
           <Button
             type="submit"
             loading={formLoading}
             disabled={formLoading || !title}
           >
             Save Changes
           </Button>
         </div>
       </form>
     </Modal>
     
     {/* Delete Confirmation Modal */}
     <Modal
       open={isDeleteModalOpen}
       onClose={() => setIsDeleteModalOpen(false)}
       title="Delete Task"
       size="sm"
     >
       {formError && (
         <Alert
           type="error"
           message={formError}
           dismissible
           onDismiss={() => setFormError(null)}
           className="mb-4"
         />
       )}
       
       <div className="space-y-4">
         <p className="text-sm text-gray-500">
           Are you sure you want to delete the task "{currentTodo?.title}"? This action cannot be undone.
         </p>
         
         <div className="flex justify-end space-x-3 pt-2">
           <Button
             variant="outline"
             onClick={() => setIsDeleteModalOpen(false)}
           >
             Cancel
           </Button>
           <Button
             variant="danger"
             onClick={handleDeleteTodo}
             loading={formLoading}
             disabled={formLoading}
           >
             Delete
           </Button>
         </div>
       </div>
     </Modal>
   </AdminLayout>
 );
};

export default TodoList;